import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import SplitAllocationModal from "./SplitAllocationModal";
import ProductivityAdjustmentsPanel from "./ProductivityAdjustmentsPanel";
import { getElementMethodsValue } from "../utils/accessMethods";
import { parseSplitsValue } from "../utils/percentSplits";

const normalizeMethodKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

// Read a bay's damage combination (id + display code) regardless of casing.
const getBayCombo = (el) => {
  const id =
    el?.corrosionDamageCombinationId ?? el?.corrosion_damage_combination_id;
  if (!id) return null;
  const combo =
    el?.corrosionDamageCombination || el?.corrosion_damage_combination;
  const code = combo?.combinationCode || combo?.combination_code || `#${id}`;
  return { id: Number(id), code: String(code) };
};

/**
 * Access method splits for area-data elements, with the global productivity
 * adjustment matrix alongside. The form itself is SplitAllocationModal; this
 * supplies the method vocabulary and the productivity side panel.
 */
const AccessMethodsModal = ({
  element,
  mode,
  targets,
  methodOptions,
  onAddMethodOption,
  getLevelLabel,
  onSave,
  onClose,
  productivityMatrix = null,
  onSaveAdjustments,
  productivityAdjustmentsUrl,
}) => {
  const isParentMode = mode === "parent";
  const targetLevel = targets.length
    ? targets[0].levelNumber || targets[0].level_number
    : (element.levelNumber || element.level_number) + 1;
  const targetLevelLabel = getLevelLabel(targetLevel);

  // Bays that will actually receive these methods on save.
  const relevantBays = useMemo(
    () => (isParentMode ? targets : [element]),
    [isParentMode, targets, element],
  );

  const relevantCombos = useMemo(() => {
    const byId = new Map();
    relevantBays.forEach((bay) => {
      const combo = getBayCombo(bay);
      if (combo && !byId.has(combo.id)) byId.set(combo.id, combo);
    });
    return Array.from(byId.values());
  }, [relevantBays]);

  const baysMissingCombo = useMemo(
    () => relevantBays.some((bay) => !getBayCombo(bay)),
    [relevantBays],
  );

  const baseValueMap = useMemo(() => {
    const map = new Map();
    (productivityMatrix?.adjustments || []).forEach((row) => {
      map.set(
        `${Number(row.corrosionDamageCombinationId)}|${Number(
          row.accessMethodSystemListId,
        )}`,
        Number(row.productivityPercentage),
      );
    });
    return map;
  }, [productivityMatrix]);

  // Methods stored on the element(s) being edited — highlighted in the panel.
  const splitMethodIds = useMemo(() => {
    const storedKeys = new Set(
      relevantBays
        .flatMap((bay) => parseSplitsValue(getElementMethodsValue(bay)))
        .map((split) => normalizeMethodKey(split.name))
        .filter(Boolean),
    );
    return new Set(
      (productivityMatrix?.accessMethods || [])
        .filter((method) =>
          storedKeys.has(normalizeMethodKey(method.accessMethodName)),
        )
        .map((method) => String(method.accessMethodSystemListId)),
    );
  }, [productivityMatrix, relevantBays]);

  // Stored methods with no productivity effect (100%) for at least one of the
  // relevant damage combinations.
  const noEffectMethodNames = useMemo(() => {
    if (!productivityMatrix) return [];
    return (productivityMatrix.accessMethods || [])
      .filter((method) =>
        splitMethodIds.has(String(method.accessMethodSystemListId)),
      )
      .filter((method) =>
        relevantCombos.some(
          (combo) =>
            (baseValueMap.get(
              `${combo.id}|${Number(method.accessMethodSystemListId)}`,
            ) ?? 100) === 100,
        ),
      )
      .map((method) => method.accessMethodName);
  }, [productivityMatrix, splitMethodIds, relevantCombos, baseValueMap]);

  const banner = (
    <>
      {noEffectMethodNames.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mt-0.5 flex-shrink-0"
          />
          <p>
            <span className="font-semibold">
              {noEffectMethodNames.join(", ")}
            </span>{" "}
            {noEffectMethodNames.length === 1 ? "is" : "are"} still at 100% for{" "}
            {relevantCombos.length === 1
              ? relevantCombos[0].code
              : "one or more of the damage combinations here"}
            , so {noEffectMethodNames.length === 1 ? "it has" : "they have"} no
            effect on base productivity yet — adjust it in the Productivity
            Adjustments panel.
          </p>
        </div>
      )}
      {baysMissingCombo && (
        <div className="mb-4 px-3 py-2 rounded bg-gray-50 border border-gray-200 text-sm text-gray-600">
          Some selected {targetLevelLabel.toLowerCase()}(s) have no damage
          combination set, so no productivity adjustment applies to them.
        </div>
      )}
    </>
  );

  const sidePanel =
    typeof onSaveAdjustments === "function" ? (
      <aside className="w-full flex-shrink-0 border-t border-gray-200 bg-white lg:w-[27rem] lg:border-l lg:border-t-0">
        <div className="lg:sticky lg:top-0 lg:max-h-[85vh]">
          <ProductivityAdjustmentsPanel
            matrix={productivityMatrix}
            onSave={onSaveAdjustments}
            highlightMethodIds={splitMethodIds}
            manageAllUrl={productivityAdjustmentsUrl}
          />
        </div>
      </aside>
    ) : null;

  return (
    <SplitAllocationModal
      element={element}
      mode={mode}
      targets={targets}
      getLevelLabel={getLevelLabel}
      onSave={({ elementIds, value }) =>
        onSave({ elementIds, accessMethods: value })
      }
      onClose={onClose}
      options={methodOptions}
      onAddOption={onAddMethodOption}
      getElementValue={getElementMethodsValue}
      title="Access Methods"
      itemLabel="access method"
      itemLabelPlural="access methods"
      selectPlaceholder="Select access method..."
      summaryLabel="Current method areas"
      banner={banner}
      sidePanel={sidePanel}
    />
  );
};

export default AccessMethodsModal;
